import { Box, Package, Search, SearchIcon } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { TInventory } from "~/types";
import { capitalize } from "~/utils";
import { useDeconsolidateStore } from "./deconsolidate.store";

type Props = {};

export const SelectPieces: FC<Props> = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounceValue(searchTerm, 500);

  const { selectedContainer, selectedPieces, setSelectedPieces } =
    useDeconsolidateStore();

  // Filter contained inventories based on search
  const filteredPieces = useMemo(() => {
    if (!selectedContainer?.containedInventories) return [];

    if (!debouncedSearch) return selectedContainer.containedInventories;

    const searchLower = debouncedSearch.toLowerCase();
    return selectedContainer.containedInventories.filter(
      (piece) =>
        piece.title?.toLowerCase().includes(searchLower) ||
        piece.inventoryRef?.toLowerCase().includes(searchLower) ||
        piece.artistName?.toLowerCase().includes(searchLower)
    );
  }, [selectedContainer?.containedInventories, debouncedSearch]);

  // Handle piece selection/deselection
  const togglePieceSelection = (piece: TInventory) => {
    if (selectedPieces.some((p) => p.id === piece.id)) {
      setSelectedPieces(selectedPieces.filter((p) => p.id !== piece.id));
    } else {
      setSelectedPieces([...selectedPieces, piece]);
    }
  };

  const handleFilterClick = () => {
    setSearchTerm(searchInput);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchTerm(searchInput);
    }
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <h2 className="font-medium">Select Pieces to Remove</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose which pieces to remove from {selectedContainer?.title} (
          {selectedContainer?.inventoryRef})
        </p>
      </div>

      <div className="p-4 border-b">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div className="flex items-center">
            <Box size={16} className="text-blue-600 mr-2 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium">{selectedContainer?.title}</div>
              <div className="text-sm text-gray-600">
                {selectedContainer?.inventoryRef} •{" "}
                {capitalize(selectedContainer?.containerType ?? "")}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {selectedContainer?.location?.name}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm">
            <div className="font-medium">Container Contents</div>
            <div className="text-gray-500">
              {filteredPieces.length} of{" "}
              {selectedContainer?.containedInventories?.length || 0} pieces
            </div>
          </div>

          {selectedContainer?.containedInventories &&
            selectedContainer.containedInventories.length > 0 && (
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <input
                    type="text"
                    className="w-64 pl-8 pr-4 py-1.5 border rounded-lg text-sm"
                    placeholder="Search pieces in container..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleKeyDown}
                  />
                  <Search
                    className="absolute left-2.5 top-2 text-gray-400"
                    size={14}
                  />
                </div>
                <button
                  className="px-2 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center space-x-1 text-sm"
                  onClick={handleFilterClick}
                >
                  <SearchIcon size={14} />
                  <span>Search</span>
                </button>
              </div>
            )}
        </div>
      </div>

      {filteredPieces.length > 0 ? (
        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {filteredPieces.map((piece) => (
            <div
              key={piece.id}
              className={`p-3 border rounded-lg ${
                selectedPieces.some((p) => p.id === piece.id)
                  ? "bg-blue-50 border-blue-200"
                  : "hover:bg-gray-50"
              } cursor-pointer`}
              onClick={() => togglePieceSelection(piece)}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedPieces.some((p) => p.id === piece.id)}
                    onChange={() => togglePieceSelection(piece)}
                  />
                  <div
                    className={`p-2 rounded-lg ${
                      selectedPieces.some((p) => p.id === piece.id)
                        ? "bg-blue-100"
                        : "bg-gray-100"
                    }`}
                  >
                    <Package
                      size={18}
                      className={
                        selectedPieces.some((p) => p.id === piece.id)
                          ? "text-blue-600"
                          : "text-gray-600"
                      }
                    />
                  </div>
                  <div>
                    <div className="font-medium">{piece.title}</div>
                    <div className="text-sm text-gray-500">
                      {piece.inventoryRef}
                    </div>
                    <div className="flex items-center mt-1 space-x-3">
                      <div className="text-sm">{piece.artistName}</div>
                      <div className="text-sm text-gray-500">
                        {piece.length}" × {piece.width}" × {piece.height}"
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm">Medium: {piece.medium}</div>
                  {piece.value && (
                    <div className="text-sm text-gray-500">
                      Value: ${piece.value.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {piece.specialHandling && piece.specialHandling.length > 0 && (
                <div className="mt-2">
                  <div className="text-sm text-gray-500">Special Handling:</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {piece.specialHandling.map(
                      (handling: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                        >
                          {capitalize(handling)}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : selectedContainer?.containedInventories &&
        selectedContainer.containedInventories.length > 0 ? (
        <div className="p-8 text-center">
          <Package size={40} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700">
            No pieces match your search
          </h3>
          <p className="text-gray-500 mt-1">
            Try adjusting your search terms or clear the search to see all
            pieces
          </p>
        </div>
      ) : (
        <div className="p-8 text-center">
          <Package size={40} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700">
            No pieces found in this container
          </h3>
          <p className="text-gray-500 mt-1">
            Try selecting a different container
          </p>
        </div>
      )}
    </div>
  );
};
